// General settings — the attendance group: how the mobile app tracks a shift
// and what a punch needs. GET general-settings/fetch-attendance-settings
// returns the company's setting as { _id, details } — both null while it was
// never created, which is what the save branch keys on: no id → POST
// add-attendance-settings, id → PATCH update-attendance-settings/:id. Both
// take the same body, which the server stores whole as the setting's details.
// Period (minutes), distance (metres) and retention (days) travel as NUMBERS,
// and the UI model keeps the wire names, so the save body is the model itself.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** Capture interval presets, in minutes (display order). */
export type AttendanceTrackingInterval = 1 | 5 | 15 | 30 | 60;
export const TRACKING_INTERVALS: readonly AttendanceTrackingInterval[] = [1, 5, 15, 30, 60];

/** Distance-threshold presets, in metres: 100 m steps up to 2 km. */
export const TRACKING_DISTANCES: readonly number[] = Array.from(
  { length: 20 },
  (_, i) => (i + 1) * 100,
);

/** Retention bounds, in days (the backend's accepted range). */
export const RETENTION_MIN_DAYS = 7;
export const RETENTION_MAX_DAYS = 180;

/** The create / update body — stored whole as the setting's details. */
export interface SaveAttendanceSettingsInput {
  /** Minutes between tracked positions while someone is on shift. */
  trackLocationPeriod: AttendanceTrackingInterval;
  /** Metres of movement that trigger a new point. */
  trackLocationDistance: number;
  /** Days the tracked positions are kept. */
  retentionPeriod: number;
  /** People must check in to start a shift. */
  checkInRequired: boolean;
  /** Check-in / check-out are blocked outside the assigned area. */
  insideGeoFenceRequired: boolean;
  /** Off = only the check-in and check-out points are captured. */
  isTrackingEnabled: boolean;
  /** Punches without a photo are blocked. */
  attendanceMediaRequired: boolean;
}

export interface AttendanceSettingsData extends SaveAttendanceSettingsInput {
  /** null = settings never created for this company (first save creates). */
  id: string | null;
}

/** What a company sees before it ever saves — the pre-fetch seed AND the
 *  never-created mapping share these values, so the drawer never disagrees
 *  with the wire. Every flag is OFF until the company opts in; the numeric
 *  presets are the mock's. */
export const DEFAULT_ATTENDANCE_SETTINGS: SaveAttendanceSettingsInput = {
  trackLocationPeriod: 15,
  trackLocationDistance: 500,
  retentionPeriod: 30,
  checkInRequired: false,
  insideGeoFenceRequired: false,
  isTrackingEnabled: false,
  attendanceMediaRequired: false,
};

interface ApiAttendanceSettings {
  _id: string | null;
  /** The last saved body; null while settings were never created. */
  details: {
    trackLocationPeriod: number;
    trackLocationDistance: number;
    retentionPeriod: number;
    checkInRequired: boolean;
    insideGeoFenceRequired: boolean;
    isTrackingEnabled: boolean;
    attendanceMediaRequired: boolean;
  } | null;
}

/** A saved value when it is one of the presets, else the default — so the
 *  drawer always shows (and saves) a value it offers. */
const toPreset = <T extends number>(value: number, presets: readonly T[], fallback: T): T =>
  presets.find((preset) => preset === value) ?? fallback;

/** Retention within the accepted range (a stale out-of-range row still edits). */
export const clampRetention = (days: number): number =>
  Math.min(RETENTION_MAX_DAYS, Math.max(RETENTION_MIN_DAYS, days));

export const toAttendanceSettings = ({
  _id,
  details,
}: ApiAttendanceSettings): AttendanceSettingsData =>
  details
    ? {
        id: _id,
        trackLocationPeriod: toPreset(
          details.trackLocationPeriod,
          TRACKING_INTERVALS,
          DEFAULT_ATTENDANCE_SETTINGS.trackLocationPeriod,
        ),
        trackLocationDistance: toPreset(
          details.trackLocationDistance,
          TRACKING_DISTANCES,
          DEFAULT_ATTENDANCE_SETTINGS.trackLocationDistance,
        ),
        retentionPeriod: clampRetention(details.retentionPeriod),
        checkInRequired: details.checkInRequired,
        insideGeoFenceRequired: details.insideGeoFenceRequired,
        isTrackingEnabled: details.isTrackingEnabled,
        attendanceMediaRequired: details.attendanceMediaRequired,
      }
    : { id: _id, ...DEFAULT_ATTENDANCE_SETTINGS };

export const generalSettingsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAttendanceSettings: build.query<AttendanceSettingsData, void>({
      query: () => ({ ...ROUTES.generalSettings.fetchAttendanceSettings }),
      transformResponse: toAttendanceSettings,
      providesTags: [{ type: 'AttendanceSettings', id: 'CURRENT' }],
    }),
    addAttendanceSetting: build.mutation<void, SaveAttendanceSettingsInput>({
      query: (body) => ({ ...ROUTES.generalSettings.addAttendanceSetting, data: body }),
      invalidatesTags: [{ type: 'AttendanceSettings', id: 'CURRENT' }],
    }),
    updateAttendanceSettings: build.mutation<void, SaveAttendanceSettingsInput & { id: string }>({
      query: ({ id, ...body }) => ({
        ...ROUTES.generalSettings.updateAttendanceSettings(id),
        data: body,
      }),
      invalidatesTags: [{ type: 'AttendanceSettings', id: 'CURRENT' }],
    }),
  }),
});

export const {
  useGetAttendanceSettingsQuery,
  useAddAttendanceSettingMutation,
  useUpdateAttendanceSettingsMutation,
} = generalSettingsApi;
